// The atlas cells of the detonation looks (R2079, `ui-map-interface.md`
// §6.3).
//
// The batches fill no gradient and no polygon, so five kinds of cell carry
// what the cards of `archive/mockups/065-torpedo-arrivals.html` and
// `archive/mockups/131-torpedo-buff-forms.html` fill:
//
//   | key                                  | what it holds                       |
//   | ------------------------------------ | ----------------------------------- |
//   | `det:g:<stops>`                      | one radial gradient, its inks       |
//   | `det:hex`                            | a white pointy-topped hex           |
//   | `det:shard:<R>:<pwQ>:<seed>:<i>`     | shard `i` of card 04, white         |
//   | `det:boil:<R>:<seed>`                | the ragged boil of card 07, white   |
//   | `det:quiet:<R>:<seed>:<step>`        | the calmed boil of card 07, white   |
//
// `detonation-recipe-kit.ts` and the recipes write a key, and this module
// answers it, the rule `combatCellKey` and `combatSpecFor` follow: one
// spelling, two readers. `fast/gfx/atlas.ts` asks it.
//
// A gradient cell holds the card's INKS, so the quad draws it with a white
// tint, and its stops hold their alpha as a share of the largest one, which
// the quad's alpha restores. A shape cell is white and the quad tints it.
//
// This is a leaf (`leaves.test.ts`). It draws on a plain 2D context.

import type { AtlasSpec } from '../fast/gfx/atlas';
import {
  DET_ALPHA_STEPS, DET_GLOW_CELL_PX, DET_HEX_CELL_R, DET_HEX_KEY, TAU, clamp01,
} from './detonation-recipe-kit';
import {
  BOIL_SWING_MID, boilNotch, boilOf, shardsOf,
} from './detonation-shapes';
import { rgba } from './color';
import { hexPath } from './marks';

/** The supersampling of a shape cell, and the scratch it must fit: a cell
 *  of radius R at this scale spans 2R times it in device px. */
const SHAPE_SCALE = 4;
const SHAPE_SCRATCH_PX = 480;
/** The hex cell's supersampling. */
const HEX_SCALE = 4;

/** The supersampling of a shape cell of radius `R`, held inside the
 *  scratch sheet. */
function shapeScale(R: number): number {
  return Math.max(1, Math.min(SHAPE_SCALE, SHAPE_SCRATCH_PX / Math.max(1, 2 * R)));
}

/** The cell of a key, or null for a key this module does not own. */
export function detonationSpecFor(key: string): AtlasSpec | null {
  if (!key.startsWith('det:')) return null;
  const p = key.split(':');
  switch (p[1]) {
    case 'g': return glowSpec(key, p[2] ?? '');
    case 'hex': return key === DET_HEX_KEY ? hexSpec(key) : null;
    case 'shard': return p.length === 6 ? shardSpec(key, +p[2], +p[3], +p[4], +p[5]) : null;
    case 'boil': return p.length === 4 ? boilSpec(key, +p[2], +p[3]) : null;
    case 'quiet': return p.length === 5 ? quietSpec(key, +p[2], +p[3], +p[4]) : null;
    default: return null;
  }
}

/**
 * One gradient: the card's `createRadialGradient` over a disc of the cell's
 * radius, stop for stop. The disc stands one texel inside the box, so its
 * rim keeps a ramp of one texel.
 */
function glowSpec(key: string, stops: string): AtlasSpec | null {
  const list = stops.split('_').map((s) => s.split('.'));
  if (list.length < 2 || list.some((s) => s.length !== 3)) return null;
  const size = DET_GLOW_CELL_PX, r = size / 2 - 1;
  return {
    key,
    scale: 1,
    box: { x: -size / 2, y: -size / 2, w: size, h: size },
    draw(ctx) {
      const g = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
      for (const [o, col, share] of list) {
        g.addColorStop(clamp01(+o / 1000), rgba(parseInt(col, 16), +share / DET_ALPHA_STEPS));
      }
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, TAU);
      ctx.fillStyle = g;
      ctx.fill();
    },
  };
}

/** The hex of card 08 at radius `DET_HEX_CELL_R`: the page's `hexPath`. */
function hexSpec(key: string): AtlasSpec {
  const r = DET_HEX_CELL_R;
  return {
    key,
    scale: HEX_SCALE,
    box: { x: -r, y: -r, w: 2 * r, h: 2 * r },
    draw(ctx) {
      ctx.beginPath();
      hexPath(ctx, 0, 0, r);
      ctx.fillStyle = '#ffffff'; // mask
      ctx.fill();
    },
  };
}

/** Shard `i` of card 04, its origin on its centroid, so a quad scales it
 *  about the centroid as the card does. */
function shardSpec(key: string, R: number, pwQ: number, seed: number, i: number): AtlasSpec | null {
  if (!(R > 0)) return null;
  const s = shardsOf(R, pwQ, seed)[i];
  if (!s) return null;
  return {
    key,
    scale: shapeScale(R),
    draw(ctx) {
      ctx.beginPath();
      s.pts.forEach(([x, y], q) => {
        if (q === 0) ctx.moveTo(x - s.cx, y - s.cy);
        else ctx.lineTo(x - s.cx, y - s.cy);
      });
      ctx.closePath();
      ctx.fillStyle = '#ffffff'; // mask
      ctx.fill();
    },
  };
}

/** The boil of card 07 at scale 1, its origin on the impact. Each notch
 *  stands at the middle of its swing; the stroke round it swings. */
function boilSpec(key: string, R: number, seed: number): AtlasSpec | null {
  return boilAt(key, R, seed, BOIL_SWING_MID);
}

/** The calmed boil of the screen form of card 07 (R2842): each notch at
 *  `step / QUIET_STEPS` of the middle of its swing. */
function quietSpec(key: string, R: number, seed: number, step: number): AtlasSpec | null {
  if (!Number.isInteger(step) || step < 0 || step >= QUIET_STEPS) return null;
  return boilAt(key, R, seed, (BOIL_SWING_MID * step) / QUIET_STEPS);
}

/** The boil of card 07 at scale 1 with every notch swung by `swing`. */
function boilAt(key: string, R: number, seed: number, swing: number): AtlasSpec | null {
  if (!(R > 0)) return null;
  const b = boilOf(R, seed);
  return {
    key,
    scale: shapeScale(R),
    draw(ctx) {
      ctx.beginPath();
      for (let k = 0; k < b.M; k++) {
        const a = (k / b.M) * TAU, r = boilNotch(b, R, k, 1, swing);
        if (k === 0) ctx.moveTo(Math.cos(a) * r, Math.sin(a) * r);
        else ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
      }
      ctx.closePath();
      ctx.fillStyle = '#ffffff'; // mask
      ctx.fill();
    },
  };
}

/** The key of shard `i` of the shard set of a drawn radius, a weight in
 *  thousandths and a seed. */
export function shardKey(R: number, pwQ: number, seed: number, i: number): string {
  return `det:shard:${R}:${pwQ}:${seed}:${i}`;
}

/** The key of the boil of a drawn radius and a seed. */
export function boilKey(R: number, seed: number): string {
  return `det:boil:${R}:${seed}`;
}

/** The steps the depth of the calmed boil takes: a boil that calms bakes
 *  this many cells at most, and the last step is the boil cell itself. */
const QUIET_STEPS = 8;

/** The key of the boil of a drawn radius and a seed with its notches at
 *  `depth` (0 smooth, 1 the boil cell) of their middle swing, on the
 *  nearest of `QUIET_STEPS` steps. */
export function quietKey(R: number, seed: number, depth: number): string {
  const step = Math.round(clamp01(depth) * QUIET_STEPS);
  return step >= QUIET_STEPS ? boilKey(R, seed) : `det:quiet:${R}:${seed}:${step}`;
}
